import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import * as jwt_decode from 'jwt-decode';

import { AppState } from '../../redux/reducers';
import * as TenantService from '../../services/Tenant.Services';
import { TenantInfo, tenantCacheKey } from './TopBar.Functions';

const readCache = (tenantId: string): TenantInfo | undefined => {
    try {
        const raw = sessionStorage.getItem(tenantCacheKey(tenantId));
        return raw ? (JSON.parse(raw) as TenantInfo) : undefined;
    } catch {
        return undefined;
    }
};

const writeCache = (tenant: TenantInfo): void => {
    try {
        sessionStorage.setItem(tenantCacheKey(tenant.tenantId), JSON.stringify(tenant));
    } catch {
        // Sin almacenamiento disponible (modo privado, cuota): se vuelve a pedir la próxima vez.
    }
};

/**
 * El tenant del usuario logueado.
 *
 * El id sale del token; el nombre se pide a Identity una sola vez y se guarda por
 * sesión, porque no cambia mientras dura y la barra se monta en cada pantalla. Si
 * el pedido falla, el header simplemente no muestra el nombre: no es motivo para
 * romper la barra ni para avisarle nada al usuario.
 */
export const useCurrentTenant = (): TenantInfo | undefined => {
    const token = useSelector((state: AppState) => (state as any)?.oidc?.user?.access_token as string | undefined);

    const tenantId = useMemo<string | undefined>(() => {
        if (!token) return undefined;
        try {
            return (jwt_decode.jwtDecode(token) as { tenantId?: string })?.tenantId;
        } catch {
            return undefined;
        }
    }, [token]);

    const [tenant, setTenant] = useState<TenantInfo | undefined>(() => (tenantId ? readCache(tenantId) : undefined));

    useEffect(() => {
        if (!token || !tenantId) {
            setTenant(undefined);
            return;
        }

        const cached = readCache(tenantId);
        if (cached) {
            setTenant(cached);
            return;
        }

        let cancelled = false;
        TenantService.GetTenantById(tenantId, token)
            .then((result) => {
                if (cancelled || !result) return;
                setTenant(result);
                writeCache(result);
            })
            .catch((error) => {
                // Se deja rastro en consola para diagnosticar (por ejemplo, un 401 si
                // Identity no acepta el token de esta app), sin molestar al usuario.
                console.warn('No se pudo obtener el nombre del tenant:', error);
            });

        return () => {
            cancelled = true;
        };
    }, [token, tenantId]);

    return tenant;
};
